import React from "react";
import { Flag } from "lucide-react";
import { MARK_DATA_URL } from "@/assets/mark";

// Branded, screenshot-ready impact card (1.9:1-ish). Used in dashboard preview + public page.
export default function ImpactCard({ data, forwardRef }) {
  const {
    display_name, milestone, moves_created, blockers_cleared, actions_completed, action_rate,
    time_saved_label, money_impact, minutescore, tier, score_status,
  } = data;

  const stats = [
    { v: moves_created ?? blockers_cleared ?? 0, l: "moves created" },
    { v: actions_completed ?? 0, l: "actions completed" },
    { v: `${action_rate ?? 0}%`, l: "follow-through" },
  ];
  if (time_saved_label !== undefined) stats.push({ v: time_saved_label, l: "decision time saved" });
  const showScore = minutescore !== undefined && minutescore !== null;

  return (
    <div ref={forwardRef} data-testid="impact-card"
      className="relative w-full overflow-hidden rounded-2xl border border-white/12 p-8 sm:p-10"
      style={{ background: "linear-gradient(155deg, #15181D 0%, #0B0C0E 60%, #0A0705 100%)" }}>
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,107,53,0.22), transparent 70%)" }} />

      <div className="relative z-10">
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center"><img src={MARK_DATA_URL} alt="MinuteSwap" className="h-7 w-7 rounded-md" /></span>
          <span className="font-head text-base font-extrabold tracking-tight text-white">MinuteSwap Impact</span>
        </div>

        {milestone && (
          <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-[var(--amber)]/40 bg-[var(--amber)]/10 px-3 py-1 font-mono2 text-[10px] font-semibold uppercase tracking-wider text-[var(--amber)]">
            <Flag className="h-3 w-3" /> {milestone}
          </span>
        )}

        <p className="font-head mt-4 text-2xl font-black tracking-tight text-white sm:text-3xl">{display_name}</p>

        <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          {stats.map((s, i) => (
            <div key={i}>
              <p className="font-head text-2xl font-black text-white sm:text-3xl">{s.v}</p>
              <p className="font-mono2 mt-0.5 text-[9px] uppercase tracking-wider text-neutral-400">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-white/10 pt-5">
          {money_impact !== undefined && (
            <div className="flex items-baseline gap-1.5">
              <span className="font-head text-xl font-black text-[var(--emerald)]">£{money_impact}</span>
              <span className="font-mono2 text-[9px] uppercase tracking-wider text-neutral-400">reported impact</span>
            </div>
          )}
          {showScore && (
            <div className="ml-auto flex items-center gap-2 rounded-full bg-white/[0.05] px-3 py-1.5">
              <span className="font-mono2 text-[9px] uppercase tracking-wider text-neutral-400">{score_status === "provisional" ? "Provisional MinuteScore" : "MinuteScore"}</span>
              <span className="font-head text-lg font-black text-white">{minutescore}</span>
              {tier && <span className="text-[10px] font-medium text-[var(--amber)]">{tier}</span>}
            </div>
          )}
        </div>

        <p className="font-mono2 mt-6 text-[10px] uppercase tracking-[0.2em] text-neutral-500">One problem. One move. · MinuteSwap</p>
      </div>
    </div>
  );
}
